import React, { useEffect, useState } from "react";
import styled from "styled-components";
import momentTz from "moment-timezone";
import { ChevronDown } from "@sonder/ui/source/icons";
import { DateTimePicker } from "@sonder/ui/source/styled/components/DateTimePicker";
import { Dropdown } from "@sonder/ui/source/styled/components/Dropdown";
import { DropdownContent } from "@sonder/ui/source/styled/components/DropdownContent";
import {
  Modal,
  ModalHeader,
  ModalProps,
} from "@sonder/ui/source/styled/components/Modal";
import { RadioSelector } from "@sonder/ui/source/styled/components/RadioSelector";
import { SCHEDULER_ROUNDING } from "@sonder/ui/source/styled/components/ScheduleLayer/constants";
import { roundToInterval } from "@sonder/ui/source/styled/components/ScheduleLayer/helpers";
import { TimePicker } from "@sonder/ui/source/styled/components/TimePicker";
import { Button } from "@sonder/ui/source/styled/fragments/Button";
import { InputField } from "@sonder/ui/source/styled/fragments/InputField";
import { Label } from "@sonder/ui/source/styled/fragments/Label";
import { ModalFooter } from "@sonder/ui/source/styled/fragments/Modal";
import { PrimaryButton } from "@sonder/ui/source/styled/fragments/PrimaryButton";
import { OptionType, ValueType } from "@sonder/ui/source/styled/types";
import { useUpdateTask } from "core/queries/UpdateTask";
import { titlelizeCase } from "core/utils/titlelizeCase";
import { ReasonForManualCompletion, TaskStatus } from "typings/globalGQLTypes";

interface CompleteTaskModal extends Omit<ModalProps, "children"> {
  taskId: string;
}

const reasonsForManualCompletion: OptionType[] = Object.keys(
  ReasonForManualCompletion,
).map(reason => ({
  value: reason,
  displayName: titlelizeCase(reason),
}));

const createDateTrigger = (date: moment.Moment) => (
  <Button iconRight>
    {date.format("MMMM Do YYYY")}
    <ChevronDown />
  </Button>
);

const BlockInputField = styled(InputField)`
  display: flex;
  flex: 0 1 auto;
`;

const TimePickerWrapper = styled.div`
  display: flex;

  > * + * {
    margin-left: 0.5rem;
  }
`;

const getCompletedAtDefaultTime = () =>
  roundToInterval(SCHEDULER_ROUNDING, momentTz()).seconds(0).milliseconds(0);

export const CompleteTaskModal = ({
  isOpen,
  onClose,
  taskId,
}: CompleteTaskModal) => {
  const [completedAt, setCompletedAt] = useState(getCompletedAtDefaultTime());
  const [reasonForManualCompletion, setReasonForManualCompletion] =
    useState<ValueType | null>(null);
  const [onUpdateTask, { loading: isUpdating }] = useUpdateTask({
    onCompleted: () => {
      if (onClose) onClose();
    },
    refetchQueries: [
      // Top Level Metrics for /schedule
      "GetSchedulerMetrics",
      // List of Cleans for /schedule
      "GetTaskSchedules",
      // List of Tasks for /tasks
      "GetTasks",
      // Task Details Panel
      "GetTask",
    ],
  });
  useEffect(() => {
    if (isOpen) {
      setCompletedAt(getCompletedAtDefaultTime());
      setReasonForManualCompletion(null);
    }
  }, [isOpen]);
  const isCreateDisabled = !reasonForManualCompletion;
  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      {({ closeModal }) => (
        <>
          <ModalHeader
            title="Manually Complete Cleaning"
            onClose={closeModal}
          />
          <>
            <BlockInputField>
              <Label>Actual time cleaning ended</Label>
              <TimePickerWrapper>
                <Dropdown trigger={createDateTrigger(completedAt)}>
                  {({ isOpen: isDropdownOpen }) =>
                    isDropdownOpen && (
                      <DropdownContent>
                        <DateTimePicker
                          date={completedAt}
                          showTimePicker={false}
                          onDateChange={nextDate => {
                            if (nextDate) setCompletedAt(nextDate);
                          }}
                        />
                      </DropdownContent>
                    )
                  }
                </Dropdown>
                <TimePicker
                  date={completedAt}
                  onChange={nextDate => {
                    if (nextDate) setCompletedAt(nextDate);
                  }}
                />
              </TimePickerWrapper>
            </BlockInputField>
            <BlockInputField>
              <Label>
                Why does this cleaning need to be manually completed?
              </Label>
              <RadioSelector
                required
                label="Select Reason..."
                options={reasonsForManualCompletion}
                selected={reasonForManualCompletion}
                onChange={value => {
                  setReasonForManualCompletion(value);
                }}
              />
            </BlockInputField>
          </>
          <ModalFooter>
            <PrimaryButton
              disabled={isUpdating || isCreateDisabled || !taskId}
              onClick={() => {
                if (taskId)
                  onUpdateTask({
                    variables: {
                      status: TaskStatus.complete,
                      statusUpdatedAt: completedAt,
                      taskId,
                      typeStructuredMetadata: {
                        clean: {
                          reasonForManualCompletion:
                            reasonForManualCompletion as ReasonForManualCompletion,
                        },
                      },
                    },
                  });
              }}
            >
              Complete
            </PrimaryButton>
          </ModalFooter>
        </>
      )}
    </Modal>
  );
};
